import { useMemo, useState } from "react";
import type { CrmLead } from "@/types/crm";
import { KanbanColumn } from "@/components/booking/KanbanColumn";

export const KANBAN_COLUMNS = [
  {
    id: "prospecting",
    title: "Prospecting",
    description: "Venues you plan to pitch. Not contacted yet.",
    accent: "text-emerald-300",
  },
  {
    id: "outreach",
    title: "Outreach",
    description: "Contacted — waiting to hear back.",
    accent: "text-amber-300",
  },
  {
    id: "negotiation",
    title: "Negotiation",
    description: "Holds and offers being discussed.",
    accent: "text-sky-300",
  },
  {
    id: "completed",
    title: "Booked",
    description: "Confirmed shows.",
    accent: "text-emerald-300",
  },
  {
    id: "closed",
    title: "Closed",
    description: "Passed, declined or archived.",
    accent: "text-muted-foreground",
  },
] as const;

export type KanbanStageId = (typeof KANBAN_COLUMNS)[number]["id"];

type LeadsKanbanBoardProps<T extends CrmLead> = {
  leads: T[];
  onSelectLead: (lead: T) => void;
  onMoveLead: (lead: T, nextStage: KanbanStageId) => void;
  canDragLead?: (lead: T) => boolean;
  renderActions?: (lead: T) => React.ReactNode;
  selectedLeadIds?: number[];
  onToggleLeadSelection?: (lead: T) => void;
  onEditLead?: (lead: T) => void;
};

export const deriveLeadStage = (lead: CrmLead): KanbanStageId => {
  const status = (lead.status || "").toLowerCase();
  const stage = (lead.pipeline_stage || "").toLowerCase();
  if (["closed", "lost", "denied", "dismissed"].includes(status)) {
    return "closed";
  }
  if (stage.includes("closed")) {
    return "closed";
  }
  if (
    status === "confirmed" ||
    status === "sent" ||
    stage.includes("completed") ||
    stage.includes("confirmed") ||
    stage.includes("won") ||
    stage.includes("booked")
  ) {
    return "completed";
  }
  if (stage.includes("negotiation") || stage.includes("closing")) {
    return "negotiation";
  }
  if (stage.includes("outreach") || stage.includes("contact")) {
    return "outreach";
  }
  return "prospecting";
};

export function LeadsKanbanBoard<T extends CrmLead>({
  leads,
  onSelectLead,
  onMoveLead,
  canDragLead,
  renderActions,
  selectedLeadIds,
  onToggleLeadSelection,
  onEditLead,
}: LeadsKanbanBoardProps<T>) {
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const visibleColumns = useMemo(() => KANBAN_COLUMNS.filter((column) => column.id !== "closed"), []);

  const grouped = useMemo(() => {
    return leads.reduce<Record<string, T[]>>((acc, lead) => {
      const bucket = deriveLeadStage(lead);
      acc[bucket] = acc[bucket] ? [...acc[bucket], lead] : [lead];
      return acc;
    }, {});
  }, [leads]);

  const handleDrop = (stageId: KanbanStageId) => {
    if (draggingId == null) return;
    const lead = leads.find((item) => item.id === draggingId);
    if (!lead) return;
    onMoveLead(lead, stageId);
    setDraggingId(null);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory">
      {visibleColumns.map((column) => (
        <KanbanColumn
          key={column.id}
          stageId={column.id}
          title={column.title}
          description={column.description}
          leads={(grouped[column.id] ?? []) as CrmLead[]}
          accentClass={column.accent}
          onLeadSelect={(lead) => onSelectLead(lead as T)}
          onDropStage={handleDrop}
          onDragStart={(leadId) => setDraggingId(leadId)}
          onDragEnd={() => setDraggingId(null)}
          canDragLead={canDragLead as ((lead: CrmLead) => boolean) | undefined}
          renderActions={renderActions as ((lead: CrmLead) => React.ReactNode) | undefined}
          selectedLeadIds={selectedLeadIds}
          onToggleLeadSelection={onToggleLeadSelection as ((lead: CrmLead) => void) | undefined}
          onEditLead={onEditLead as ((lead: CrmLead) => void) | undefined}
        />
      ))}
    </div>
  );
}
